import { Spinner } from '../components/feedback';
import { useEffect, useState } from 'react';
import type { LauncherPolicy } from '@mcpanel/core';
import { defaultLauncherPolicy, parsePatterns, policyProblem } from '../launcher-policy';
import { launcherApi } from '../api/launcher';
import { errorMessage, useApp } from '../app-context';
import { usePoll } from '../hooks/usePoll';
import { ErrorAlert } from '../components/ui';
export function LauncherPolicyCard({ serverId }: { serverId: string }) {
  const { canOperate, notify } = useApp();
  const data = usePoll(() => launcherApi.policy(serverId), 0, [serverId]);
  const [policy, setPolicy] = useState<LauncherPolicy>(defaultLauncherPolicy);
  const [patterns, setPatterns] = useState('');
  const [memory, setMemory] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (data.data) {
      setPolicy(data.data);
      setPatterns(data.data.allowedExtraPatterns.join('\n'));
      setMemory(data.data.recommendedMemoryGb?.toString() ?? '');
    }
  }, [data.data]);
  const save = async () => {
    const value = {
      mode: policy.mode,
      allowExtraMods: policy.allowExtraMods,
      allowedExtraPatterns: parsePatterns(patterns),
      ...(memory !== '' ? { recommendedMemoryGb: Number(memory) } : {}),
    };
    const problem = policyProblem(value);
    if (problem) {
      notify(problem, true);
      return;
    }
    setBusy(true);
    try {
      await launcherApi.savePolicy(serverId, value);
      notify('構成チェックを保存しました');
      await data.reload();
    } catch (error) {
      notify(errorMessage(error), true);
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="card card-body stack">
      <h2 data-search-id="launcher-policy">ランチャーの構成チェック</h2>
      <ErrorAlert error={data.error} />
      <p className="small muted" style={{ margin: 0 }}>
        Shouchan Launcher が起動の前に、プレイヤーの Mod
        がこのサーバーと同じ構成かを確かめます。クライアント側の確認は迂回できるので、本当の強制はサーバー側で行ってください。
      </p>
      <div className="field">
        <label htmlFor={`policy-mode-${serverId}`}>構成が違うとき</label>
        <select
          id={`policy-mode-${serverId}`}
          className="input"
          style={{ maxWidth: 260 }}
          disabled={!canOperate}
          value={policy.mode}
          onChange={(e) => setPolicy({ ...policy, mode: e.target.value as LauncherPolicy['mode'] })}
        >
          <option value="off">確認しない</option>
          <option value="warn">警告する（起動はできる）</option>
          <option value="block">起動を止める</option>
        </select>
      </div>
      <label className="check" data-search-id="launcher-extra">
        <input
          type="checkbox"
          disabled={!canOperate}
          checked={policy.allowExtraMods}
          onChange={(e) => setPolicy({ ...policy, allowExtraMods: e.target.checked })}
        />
        サーバーに無い Mod を入れてもよい
      </label>
      <div className="field">
        <label htmlFor={`policy-patterns-${serverId}`}>許可する Mod の名前（任意）</label>
        <textarea
          id={`policy-patterns-${serverId}`}
          className="input"
          style={{ height: 80, padding: 8, fontFamily: 'var(--mono)' }}
          disabled={!canOperate || policy.allowExtraMods}
          value={patterns}
          onChange={(e) => setPatterns(e.target.value)}
          placeholder={'sodium\niris'}
        />
        <div className="small faint">
          jar のファイル名に含まれる名前を、1 行に 1
          つ（カンマ区切りも可）。小文字の英数字・点・ハイフン・アンダースコアで 2〜40 文字、最大 50 件です。
        </div>
      </div>
      <div className="field">
        <label htmlFor={`policy-memory-${serverId}`} data-search-id="launcher-memory">
          推奨メモリ（GB）
        </label>
        <input
          id={`policy-memory-${serverId}`}
          className="input"
          style={{ maxWidth: 260 }}
          type="number"
          min={1}
          max={64}
          step={1}
          disabled={!canOperate}
          value={memory}
          onChange={(e) => setMemory(e.target.value)}
        />
        <div className="small faint">
          ランチャーに表示します。プレイヤーのメモリの設定がこれより少ないと、合わせるボタンを出します。
        </div>
      </div>
      {canOperate && (
        <div className="row">
          <button className="btn primary" disabled={busy || !data.data} onClick={() => void save()}>
            {busy && <Spinner />}
            構成チェックを保存
          </button>
        </div>
      )}
    </section>
  );
}
